import { useFlyOutProductsBasedOnSelectedCloud } from '../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import { useNavigate, useParams } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import { useStyles } from './BuyersDetails.styles'
import Tabs from './Contracts/BuyerDetailTabs'
import { OverviewSectionWrapper } from '../FlyoutOverview/OverviewSectionWrapper/OverviewSectionWrapper'

import { Grid } from '@mui/material'
import { LoadingTypes, isLoading, useAccountId } from '@labrav/common'
import { useEffect } from 'react'
import { useDispatch, useSelector } from '../../store/hooks'
import { LoadingStateComponent } from '@labrav/common'
import { ListingCreationPage } from '../../common/components/ListingCreationPage/ListingCreationPage'
import { subscriptionsRangeStatus } from '../../utils/subscriptionsFilterMapping'
import { BuyerJourneyMap } from './BuyerJourneyMap/BuyerJourneyMap'
import {
  BuyerInfo,
  BuyerDetailRow,
} from '../../common/components/DetailRow/DetailRow'
import { ExternalLink, StatusIndicator } from '@labrav/react-components'
import { DateTime } from 'luxon'
import { MultiplesV3 } from '@labrav/react-components'
import { getBuyer } from '../../modules/buyers/action'
import { getBuyerContracts } from '../../modules/buyerContracts/action'
import { getFlyoutOverview } from '../../modules/flyoutOverview/action'
import { BuyersData } from '../../modules/buyers/types'
import { standardLabraDateFormat } from '@labrav/common'
import aws from '../../common/images/awsCloudNew.svg'
import { getBuyerContacts } from '../../modules/buyerContacts/action'
import { getBuyerRevenues } from '../../modules/buyerRevenues/action'
import { getBuyerMeterings } from '../../modules/buyerMeterings/action'
import { Meterings, Metering } from '../../modules/buyerMeterings/types'
import { useSyncFlyoutRouteParams } from '../../common/utils/hooks/useSyncFlyoutRouteParams'
import { useSyncCloudMarketplace } from '@labrav/common'

export const BuyerDetails = () => {
  useSyncFlyoutRouteParams()
  useSyncCloudMarketplace()
  const { classes } = useStyles()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const theme = useTheme()
  const timeZone = useSelector<string>(
    state => state.userProfile.userProfile?.timeZone || 'UTC'
  )
  const { buyerId = '', accountId: accountIdFromParams } = useParams<{
    buyerId?: string
    accountId?: string
  }>()

  const accountIdFromHook = useAccountId({ product: 'flyout' }) || ''
  const accountId = accountIdFromParams || accountIdFromHook || ''
  const isBuyerPageLoading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.BUYERS_OVERVIEW)
  )
  const isProductsLoading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.FETCH_PRODUCTS)
  )
  const { buyers } = useSelector<BuyersData>(state => state.BuyersOverView)
  const { meterings } = useSelector<Meterings>(state => state.BuyerMeterings)

  const calculateTotalGrowth = () => {
    if (meterings && meterings.length > 0) {
      const growths = meterings
        .filter(
          (metering: Metering) =>
            metering.subscriptionStatus === 'SUBSCRIPTION_SUCCESS'
        )
        .map((metering: Metering) => metering.growthRatePercent)
      const totalGrowth = growths.reduce((a, b) => a + b, 0)
      return totalGrowth
    }

    return 0
  }

  const getChurnProbability = () => {
    const totalGrowth = calculateTotalGrowth()
    if (meterings.length === 0) {
      return {
        tooltipMessage: 'Churn probability - Unknown',
        color: theme.palette.common.white,
        text: '--',
      }
    } else if (totalGrowth > 0) {
      return {
        tooltipMessage: 'Churn probability - Low',
        color: theme.palette.success.main,
        text: 'Low',
      }
    } else if (totalGrowth < 0) {
      return {
        tooltipMessage: 'Churn probability - High',
        color: theme.palette.error.main,
        text: 'High',
      }
    } else {
      return {
        tooltipMessage: 'Churn probability - Medium',
        color: theme.palette.warning.main,
        text: 'Medium',
      }
    }
  }
  const buyer = buyers[0] || {}
  const { products: productRows } = useFlyOutProductsBasedOnSelectedCloud()

  const renewalDate = DateTime.fromISO(buyer?.details?.renewal, {
    zone: timeZone,
  })
  const currentDate = DateTime.now().setZone(timeZone)
  const daysUntilRenewal = Math.floor(
    renewalDate.diff(currentDate, 'days').days
  )

  const renderRenewalValue = (renewal: string, activeContracts: number) => {
    if (!renewal) {
      return activeContracts > 0 ? (
        <div>--</div>
      ) : (
        <div className={classes.contractRed}>Contract ended</div>
      )
    }

    if (daysUntilRenewal < 0) {
      return <div className={classes.contractRed}>Contract ended</div>
    } else if (daysUntilRenewal <= 120) {
      return (
        <div
          className={
            daysUntilRenewal <= 90
              ? classes.contractRed
              : classes.contractOrange
          }
        >
          {daysUntilRenewal} {daysUntilRenewal === 1 ? 'day' : 'days'}
        </div>
      )
    }

    const formattedDate = renewalDate.toFormat(standardLabraDateFormat)

    return <div>{formattedDate}</div>
  }

  const getRenewalTitle = (renewal: string) => {
    if (!renewal) {
      return 'in'
    }

    if (daysUntilRenewal <= 120 && daysUntilRenewal > 0) {
      return ' in '
    } else if (daysUntilRenewal < 0) {
      return 'in'
    } else {
      return ' on '
    }
  }

  const goToSubscriptions = (status: subscriptionsRangeStatus) => {
    const subscriptionsPath = accountId
      ? `/flyout/aws/${accountId}/subscriptions`
      : '/flyout/subscriptions'
    navigate(subscriptionsPath, {
      state: {
        status: status,
      },
    })
  }

  useEffect(() => {
    if (accountId && buyerId && productRows && productRows.length) {
      dispatch(
        getBuyer({
          partnerId: accountId,
          buyerId,
        })
      )
      dispatch(
        getBuyerContracts({
          partnerId: accountId,
          buyerId,
        })
      )
      dispatch(
        getBuyerContacts({
          partnerId: accountId,
          buyerId,
        })
      )
      dispatch(
        getBuyerMeterings({
          partnerId: accountId,
          buyerId,
        })
      )
      dispatch(
        getBuyerRevenues({
          partnerId: accountId,
          buyerId,
        })
      )
      dispatch(
        getFlyoutOverview({
          partnerId: accountId,
        })
      )
    }
  }, [accountId, buyerId, productRows, dispatch])

  const awsAccounts =
    buyer?.details?.awsCloudAccountIds?.map((accountId, index) => ({
      id: String(accountId),
      logoSrc: index === 0 ? aws : '',
    })) || {}

  const accountOwners =
    buyer?.details?.creatorNames
      ?.filter(name => name !== null)
      .map(name => ({
        id: String(name),
        logoSrc: '',
        copyable: false,
      })) || {}

  return (
    <>
      {isBuyerPageLoading && isProductsLoading ? (
        <>
          <LoadingStateComponent
            heading="Loading your buyer details page"
            description="It may take a few moments, please wait..."
          />
        </>
      ) : (
        <div data-testid="buyer-details-page">
          {productRows &&
          !productRows?.length &&
          !isProductsLoading &&
          !isBuyerPageLoading ? (
            <ListingCreationPage />
          ) : (
            <Grid
              container
              spacing={3}
              sx={{
                mb: 2,
              }}
            >
              {buyer?.details?.activeContracts > 0 &&
                daysUntilRenewal >= 0 &&
                daysUntilRenewal <= 90 && (
                  <Grid
                    size={{
                      xs: 12,
                      sm: 12,
                      md: 12,
                    }}
                  >
                    <div className={classes.renewalInfo}>
                      <span>
                        Upcoming renewal for {buyer?.buyerInfo?.companyName}
                      </span>
                      <span>
                        &nbsp;{getRenewalTitle(buyer?.details?.renewal)}&nbsp;
                      </span>
                      <span>
                        {renderRenewalValue(
                          buyer?.details?.renewal,
                          buyer?.details?.activeContracts
                        )}
                      </span>
                    </div>
                  </Grid>
                )}
              <Grid
                size={{
                  xs: 12,
                  sm: 12,
                  md: 12,
                }}
              >
                <OverviewSectionWrapper
                  children={
                    buyer && (
                      <div className={classes.section}>
                        <BuyerDetailRow
                          children={
                            <>
                              <BuyerInfo
                                keyName="Website"
                                title={'Website'}
                                gridSize={2}
                                value={
                                  <ExternalLink
                                    noreferrer
                                    noopener
                                    href={`https://${buyer?.domain}`}
                                  >
                                    {buyer?.domain}
                                  </ExternalLink>
                                }
                                showDivider={false}
                              />
                              <BuyerInfo
                                title={'Buyer since'}
                                gridSize={2}
                                keyName="buyerSince"
                                value={
                                  buyer?.details?.buyerSince
                                    ? DateTime.fromISO(
                                        buyer?.details?.buyerSince,
                                        {
                                          zone: timeZone,
                                        }
                                      ).toFormat(standardLabraDateFormat)
                                    : '-'
                                }
                                showDivider={false}
                              />
                              <BuyerInfo
                                title={'Churn probability'}
                                gridSize={2}
                                keyName="churnProbability"
                                value={
                                  <StatusIndicator
                                    tooltipMessage={
                                      getChurnProbability().tooltipMessage
                                    }
                                    color={getChurnProbability().color}
                                    text={getChurnProbability().text}
                                  />
                                }
                                showDivider={false}
                              />
                              <BuyerInfo
                                title={'No. of active contracts'}
                                gridSize={2}
                                keyName="activeContracts"
                                value={`${buyer?.details?.activeContracts} out of ${buyer?.details?.totalContracts}`}
                                showDivider={false}
                              />
                              <BuyerInfo
                                title={'Account owner'}
                                gridSize={3}
                                keyName="accountOwner"
                                value={
                                  accountOwners.length >= 1 ? (
                                    <MultiplesV3 list={accountOwners} />
                                  ) : (
                                    '-'
                                  )
                                }
                                showDivider={false}
                              />
                            </>
                          }
                        />
                        <BuyerDetailRow
                          children={
                            <>
                              <BuyerInfo
                                keyName="Country"
                                title={'Country'}
                                gridSize={2}
                                value={'-'}
                                showDivider={false}
                              />
                              <BuyerInfo
                                title={'Segment'}
                                gridSize={2}
                                keyName="segment"
                                value={'-'}
                                showDivider={false}
                              />
                              <BuyerInfo
                                title={'Industry'}
                                gridSize={2}
                                keyName="industry"
                                value={'-'}
                                showDivider={false}
                              />
                              <BuyerInfo
                                title={'No. of contracts'}
                                gridSize={2}
                                keyName="totalContracts"
                                value={`${buyer?.details?.totalContracts}`}
                                showDivider={false}
                              />

                              <BuyerInfo
                                title={'Cloud account IDs'}
                                gridSize={3}
                                keyName="cloudAccountIds"
                                value={<MultiplesV3 list={awsAccounts} />}
                                showDivider={false}
                              />
                            </>
                          }
                        />
                      </div>
                    )
                  }
                  heading=""
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 12,
                  md: 12,
                }}
              >
                <OverviewSectionWrapper
                  children={<BuyerJourneyMap />}
                  heading="Buyers journey map"
                  handleAction={() =>
                    goToSubscriptions(subscriptionsRangeStatus.ACTIVE)
                  }
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 12,
                  md: 12,
                }}
              >
                <Tabs />
              </Grid>
            </Grid>
          )}
        </div>
      )}
    </>
  )
}
